import { c as _c } from "react/compiler-runtime";
import { getDefaultLocale } from '@onefootprint/core';
import { getHostedUserAuthMethodsQueryKey } from '@onefootprint/fetch-hosted';
import type { ActionKind } from '@onefootprint/request-types';
import { Button, Form, PageShell } from '@onefootprint/ui';
import { createFileRoute, getRouteApi, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { UpdateVerify } from '@/idv2/features/auth-methods/update-verify';
import { getLogger } from '@/idv2/utils/logger';
const {
  logInfo
} = getLogger({
  name: 'idv2',
  location: 'auth-methods-update-phone'
});
const parentRoute = getRouteApi('/auth-methods');
const searchSchema = z.object({
  actionKind: z.enum(['add_primary', 'replace']).catch('add_primary')
});
export const Route = createFileRoute('/auth-methods/update-phone')({
  component: UpdatePhone,
  validateSearch: searchSchema
});
type PhoneFormData = {
  phone: string;
};
function UpdatePhone() {
  const $ = _c(47);
  const navigate = useNavigate();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "authMethods"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t,
    i18n
  } = useTranslation("idv2", t0);
  const {
    actionKind
  } = Route.useSearch();
  const {
    token
  } = parentRoute.useLoaderData();
  const {
    queryClient
  } = Route.useRouteContext();
  let t1;
  if ($[1] !== i18n.language) {
    t1 = getDefaultLocale(i18n.language);
    $[1] = i18n.language;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const locale = t1;
  const [screen, setScreen] = useState("collect");
  const [phone, setPhone] = useState("");
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      defaultValues: {
        phone: ""
      }
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const methods = useForm(t2);
  let t3;
  if ($[4] !== actionKind || $[5] !== t) {
    t3 = actionKind === "replace" ? t(_temp) : t(_temp2);
    $[4] = actionKind;
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const title = t3;
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = data => {
      setPhone(data.phone);
      setScreen("verify");
    };
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const handleSubmit = t4;
  let t5;
  if ($[8] !== navigate || $[9] !== queryClient || $[10] !== token) {
    t5 = () => {
      logInfo("Phone updated");
      queryClient.invalidateQueries({
        queryKey: getHostedUserAuthMethodsQueryKey({
          headers: {
            "X-Fp-Authorization": token
          }
        })
      });
      navigate({
        to: "/auth-methods"
      });
    };
    $[8] = navigate;
    $[9] = queryClient;
    $[10] = token;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const handleSuccess = t5;
  if (screen === "verify" && phone) {
    const t6 = actionKind as ActionKind;
    let t7;
    if ($[12] !== handleSuccess || $[13] !== phone || $[14] !== t6 || $[15] !== title || $[16] !== token) {
      t7 = <UpdateVerify token={token} challengeKind="sms" actionKind={t6} contactValue={phone} title={title} onSuccess={handleSuccess} />;
      $[12] = handleSuccess;
      $[13] = phone;
      $[14] = t6;
      $[15] = title;
      $[16] = token;
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    return t7;
  }
  let t6;
  if ($[18] !== t) {
    t6 = t(_temp3);
    $[18] = t;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== methods) {
    t7 = methods.handleSubmit(handleSubmit);
    $[20] = methods;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== t) {
    t8 = t(_temp4);
    $[22] = t;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== t) {
    t9 = t(_temp5);
    $[24] = t;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== t9) {
    t10 = {
      required: t9
    };
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== locale || $[29] !== t10 || $[30] !== t8) {
    t11 = <div className="w-full"><Form.Tel label={t8} name="phone" locale={locale} rules={t10} /></div>;
    $[28] = locale;
    $[29] = t10;
    $[30] = t8;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== t) {
    t12 = t(_temp6);
    $[32] = t;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== t12) {
    t13 = <Button type="submit" variant="primary" fullWidth={true} size="large">{t12}</Button>;
    $[34] = t12;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== t11 || $[37] !== t13 || $[38] !== t7) {
    t14 = <form onSubmit={t7} className="flex flex-col gap-4 items-start w-full">{t11}{t13}</form>;
    $[36] = t11;
    $[37] = t13;
    $[38] = t7;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== methods || $[41] !== t14) {
    t15 = <FormProvider {...methods}>{t14}</FormProvider>;
    $[40] = methods;
    $[41] = t14;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== t15 || $[44] !== t6 || $[45] !== title) {
    t16 = <PageShell title={title} subtitle={t6}>{t15}</PageShell>;
    $[43] = t15;
    $[44] = t6;
    $[45] = title;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  return t16;
}
function _temp6($_4) {
  return $_4.updatePhone.continue;
}
function _temp5($_3) {
  return $_3.updatePhone.requiredError;
}
function _temp4($_2) {
  return $_2.updatePhone.label;
}
function _temp3($_1) {
  return $_1.updatePhone.subtitle;
}
function _temp2($_0) {
  return $_0.updatePhone.addTitle;
}
function _temp($) {
  return $.updatePhone.replaceTitle;
}